import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { MeProfile } from "../gql/assistant/documents/Me/MeProfile.document";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { Settings, Sparkles } from "lucide-react";
import { useAiCreditsAccess } from "@/features/ai-credits/access";

interface AccountMenuProps {
  onLogout?: () => void;
}

export const AccountMenu: React.FC<AccountMenuProps> = ({ onLogout }) => {
  const { activeAccount, logout, user, authInitialized, isAuthenticated } = useAuth();
  const apollo = useApolloClient();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { data: meData } = useQuery(MeProfile, {
    fetchPolicy: "cache-first",
    skip: !authInitialized || !isAuthenticated,
  });

  // While Credits are only observed, AI Credits is for AEC administrators.
  const aiCredits = useAiCreditsAccess({ enabled: authInitialized && isAuthenticated });

  const initials = activeAccount?.username?.slice(0, 2).toUpperCase() ?? user?.email?.slice(0, 2).toUpperCase() ?? "U";

  const handleLogout = async () => {
    setOpen(false);
    await apollo.clearStore();
    await logout();
    if (onLogout) onLogout();
  };

  const handleEditProfile = () => {
    setOpen(false);
    navigate("/profile");
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="icon" variant="ghost" aria-haspopup="menu" aria-expanded={open}>
          {initials}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-72 p-4 text-sm z-50 bg-surface-card-light border border-surface-border-light dark:bg-surface-card-dark dark:border-surface-border-dark"
      >
        <div className="mb-3 border-b border-surface-border-light pb-3 dark:border-surface-border-dark/70">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wide text-foreground-muted-light dark:text-foreground-muted-dark">Signed in as</p>
              <p className="truncate text-sm font-semibold text-foreground-light dark:text-foreground-dark">
                {activeAccount?.username || user?.email}
              </p>
              {meData?.me.profile && (
                <p className="mt-1 truncate text-xs text-foreground-muted-light dark:text-foreground-muted-dark">
                  {meData.me.profile.givenName} {meData.me.profile.surname}
                </p>
              )}
            </div>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleEditProfile} aria-label="Edit profile">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {aiCredits.visible ? (
            <Button
              type="button"
              variant="ghost"
              className="flex w-full items-center justify-between rounded-xl px-3 py-2 font-medium"
              onClick={() => {
                setOpen(false);
                navigate("/ai-credits");
              }}
            >
              AI Credits
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className="flex w-full items-center justify-between rounded-xl px-3 py-2 font-medium"
            onClick={handleLogout}
          >
            Logout
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M12.25 3a.75.75 0 0 1 0 1.5H7.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h4.75a.75.75 0 0 1 0 1.5H7.5a2.5 2.5 0 0 1-2.5-2.5v-9A2.5 2.5 0 0 1 7.5 3h4.75Zm2.03 3.22 2.5 2.5a.75.75 0 0 1 0 1.06l-2.5 2.5a.75.75 0 1 1-1.06-1.06l1.22-1.22H8a.75.75 0 0 1 0-1.5h6.44l-1.22-1.22a.75.75 0 1 1 1.06-1.06Z" />
            </svg>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountMenu;
